import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useAlertBlockedNotice } from '../../hooks/assistant/useAlertBlockedNotice';
import { useMarkWorkItemsRead } from '../../hooks/mutations/useMarkWorkItemsRead';
import { useUpdateWorkItem } from '../../hooks/mutations/useUpdateWorkItem';
import {
  ARCHIVED_WORK_ITEM_FILTERS,
  UNARCHIVED_WORK_ITEM_FILTERS,
  useWorkItems,
  WORK_ITEM_ALERTS_PAGE_SIZE,
} from '../../hooks/queries/useWorkItems';
import { useWorkItemStatusCounts } from '../../hooks/queries/useWorkItemStatusCounts';
import { useWorkItemNeedAttentionFloor } from '../../hooks/useWorkItemNeedAttentionFloor';
import { useAssistantTurnStatus } from '../../providers/AssistantTurnStatusProvider';
import { getWorkItemSectionCounts, groupWorkItemsBySection } from '../../utils/alert/alerts';
import { buildAlertsCenterView } from '../../utils/alert/alertsCenter';
import { workItemToListedAlert } from '../../utils/alert/workItemAlertRow';
import { useAlertRowAction } from '../alerts/useAlertRowAction';

import type { AlertsCenterStatus, IAlertsCenterView } from '../../utils/alert/alertsCenter';
import type { WorkItemDetailsFragment } from '../alerts/__generated__/workItemFragment.gql';

/** Rows per request, per read. The live read pages both of its sections at once. */
const PAGE_SIZE = WORK_ITEM_ALERTS_PAGE_SIZE;

/** Both reads take their filters from the hook that owns them, alongside its page size. */
const LIVE_FILTERS = UNARCHIVED_WORK_ITEM_FILTERS;
const HISTORY_FILTERS = ARCHIVED_WORK_ITEM_FILTERS;

export interface IAlertsCenterDataOptions {
  /** Skips every read — the viewer has no Notification Center. */
  skip: boolean;
  /**
   * Skips the archived (history) read alone. Only the full presentation shows
   * history, so a session that never opens the center never pays for it.
   */
  skipHistory: boolean;
}

export interface IAlertsCenterData {
  /** The household's ACTIVE total from the counts read. */
  activeCount: number;
  /** Groups with rows for `AlertsCenterBase`, every presentation reading the same array. */
  sections: IAlertsCenterView['sections'];
  /** Whole-surface state; only the full presentation renders its non-ready states. */
  status: AlertsCenterStatus;
  /** Need-attention rows fetched so far — what the truncated card cuts to its first rows. */
  needAttentionRowCount: number;
  /** The original fragment rows by id, for click routing off the relation. */
  byId: ReadonlyMap<string, WorkItemDetailsFragment>;
  isTurnInFlight: boolean;
  /** Id of the row showing the mid-turn refusal pill, or none. */
  blockedNoticeAlertId: string | null;
  /** Puts the refusal pill on a row. */
  showBlockedNotice: (alertId: string) => void;
  markRead: (alertIds: string[]) => Promise<unknown>;
  /** Runs a row's resolved action; false leaves the click to the caller. */
  runAction: ReturnType<typeof useAlertRowAction>['runAction'];
  /** The in-flight reconnect and its error line; the host renders it somewhere the card cannot cover. */
  alertActionSlot: React.ReactNode;
  handleDismiss: (alertId: string) => void;
  /** Retries every read. */
  handleRetry: () => void;
}

/**
 * The Notification Center's reads, wired to `work_items`, for whichever
 * component renders `AlertsCenterBase`.
 *
 * Two reads, not three: `getWorkItems` deliberately offers no status filter
 * (status is derived per viewer and clock), so the live read takes everything
 * unarchived and splits it client-side by the server-derived `status` into
 * need-attention and pending, while history pages independently. One
 * consequence, handled below: a `created_at`-ordered page can be all PENDING
 * while ACTIVE rows sit past the page boundary, so when the counts say active
 * work exists and the bucket is empty, the next page is fetched automatically
 * and the section stays rendered with its server count in the meantime —
 * otherwise the pip would point at rows the center never shows.
 *
 * Click routing is the caller's: it goes through `getWorkItemAlertRowAction`
 * on the original fragment row (CONFIG routes on the relation) via `runAction`
 * — the row-action router is never invoked on this path, and it answers `null`
 * for adapted rows in any case, since they carry no subtype.
 *
 * @param options.skip Skips every read — the viewer has no Notification Center.
 * @param options.skipHistory Skips the archived read alone, which only the
 *   full presentation shows.
 * @returns The rows and counts shaped for `AlertsCenterBase`, the fragment rows
 *   by id for click routing, the mid-turn refusal state, and the mutations
 *   and retry the card's controls call.
 */
export function useAlertsCenterData({ skip, skipHistory }: IAlertsCenterDataOptions): IAlertsCenterData {
  const { markRead } = useMarkWorkItemsRead();
  const { archiveWorkItem } = useUpdateWorkItem();
  const { runAction, alertActionSlot } = useAlertRowAction();
  const { isTurnInFlight } = useAssistantTurnStatus();
  const { blockedNoticeAlertId, showBlockedNotice } = useAlertBlockedNotice(isTurnInFlight);

  const live = useWorkItems({ filters: LIVE_FILTERS, pageSize: PAGE_SIZE, skip });
  const history = useWorkItems({ filters: HISTORY_FILTERS, pageSize: PAGE_SIZE, skip: skip || skipHistory });
  const { counts, loading: countsLoading, refetch: refetchCounts } = useWorkItemStatusCounts({ skip });

  // The non-blocking read fails silently on screen, so this is the only trace
  // it leaves.
  React.useEffect(() => {
    if (history.error) {
      logger.error('Failed to load the Alerts Center history section', { history: history.error });
    }
  }, [history.error]);

  // Status is derived from the request clock, so cached pages go stale by time
  // alone — a due date crossing now moves the polled counts (the sidebar polls
  // the shared entry) while the rows keep yesterday's classification, and the
  // pip ends up pointing at a section this list never shows. Refetching when a
  // status bucket transitions resyncs the rows in lockstep. `unread` is
  // deliberately outside the key: mark-read moves it on every row click, and a
  // list refetch there would blank the loaded pages the narrowed cache
  // eviction exists to preserve. A skipped history read stays skipped: Apollo
  // answers `refetch` on a standby query with a real network request, which
  // would fetch history the full center has never asked for.
  const statusBucketsKey = `${counts.active}:${counts.pending}:${counts.archived}`;
  const lastBucketsRef = React.useRef<string | null>(null);
  const { refetch: refetchLive } = live;
  const { refetch: refetchHistory } = history;
  const isHistorySkipped = skip || skipHistory;
  React.useEffect(() => {
    if (countsLoading) {
      return;
    }
    if (lastBucketsRef.current === null || lastBucketsRef.current === statusBucketsKey) {
      lastBucketsRef.current = statusBucketsKey;
      return;
    }
    lastBucketsRef.current = statusBucketsKey;
    void refetchLive();
    if (!isHistorySkipped) {
      void refetchHistory();
    }
  }, [countsLoading, statusBucketsKey, refetchLive, refetchHistory, isHistorySkipped]);

  const grouped = React.useMemo(() => groupWorkItemsBySection(live.workItems), [live.workItems]);

  // `countsLoading` is deliberately not passed: this surface resyncs on counts
  // transitions above, so the floor may chase from the first counted read.
  const { needAttentionMissing } = useWorkItemNeedAttentionFloor({
    activeCount: counts.active,
    needAttentionCount: grouped.needAttention.length,
    live,
  });

  const byId = React.useMemo(() => {
    const map = new Map<string, WorkItemDetailsFragment>();
    for (const workItem of [...live.workItems, ...history.workItems]) {
      map.set(workItem.id, workItem);
    }
    return map;
  }, [live.workItems, history.workItems]);

  const handleDismiss = React.useCallback(
    (alertId: string) => {
      void archiveWorkItem(alertId);
    },
    [archiveWorkItem]
  );

  const handleRetry = React.useCallback(() => {
    void live.refetch();
    void history.refetch();
    void refetchCounts();
  }, [live, history, refetchCounts]);

  const sectionCounts = getWorkItemSectionCounts(counts);
  const livePager = { hasMore: live.hasMore, isLoadingMore: live.isLoadingMore, onLoadMore: live.loadMore };
  const chasingNeedAttention = needAttentionMissing && live.hasMore;
  // One server list feeds two sections, so there is one pager. It sits on the
  // last non-empty live section — pending when it has rows — because a page
  // fetched from either button appends to both groups, and rows materializing
  // above the pressed button reads as the list jumping. While the auto-fetch
  // floor is chasing active rows, its synthetic section below holds the pager
  // instead.
  const pagerOnPending = grouped.pending.length > 0 && !chasingNeedAttention;

  const view = buildAlertsCenterView({
    sections: {
      needAttention: {
        rows: grouped.needAttention.map(workItemToListedAlert),
        count: sectionCounts.needAttention,
        ...(pagerOnPending ? {} : livePager),
      },
      pending: {
        rows: grouped.pending.map(workItemToListedAlert),
        count: sectionCounts.pending,
        ...(pagerOnPending ? livePager : {}),
      },
      history: {
        rows: history.workItems.map(workItemToListedAlert),
        count: sectionCounts.history,
        hasMore: history.hasMore,
        isLoadingMore: history.isLoadingMore,
        onLoadMore: history.loadMore,
      },
    },
    isLoading: live.loading || history.loading,
    hasBlockingError: live.error !== undefined,
  });

  // The floor's visible half: while the auto-fetch chases the counted-but-
  // unfetched active rows, render the section header over its own pager rather
  // than letting `buildAlertsCenterView` drop the empty group.
  const sections: IAlertsCenterView['sections'] =
    view.status === 'ready' && chasingNeedAttention
      ? [
          { section: 'needAttention', rows: [], count: sectionCounts.needAttention, ...livePager, isLoadingMore: true },
          ...view.sections,
        ]
      : view.sections;

  return {
    activeCount: counts.active,
    sections,
    status: view.status,
    needAttentionRowCount: grouped.needAttention.length,
    byId,
    isTurnInFlight,
    blockedNoticeAlertId,
    showBlockedNotice,
    markRead,
    runAction,
    alertActionSlot,
    handleDismiss,
    handleRetry,
  };
}
